import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Cover } from "../media";
import { formatDate, useLocales } from "../../core/i18n";
import { categoryLabel } from "../../core/content";

const META = "font-mono text-pp-label uppercase tracking-pp-label text-pp-muted";

/** The always-visible row: index, title, category, date and the arrow. */
const JournalCardLine = ({ entry, index, open, category, date }) => (
	<div className="grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3rem_1fr_auto_auto] items-baseline gap-x-4 sm:gap-x-6 py-5 px-4 sm:px-8 lg:px-12">
		<span
			className={`font-mono text-pp-label tracking-pp-label tabular-nums ${open ? "text-pp-accent-text" : "text-pp-muted"}`}
		>
			{String(index + 1).padStart(2, "0")}
		</span>
		<span className="min-w-0">
			<span
				className="block font-sligoil font-bold text-pp-ink text-pp-title-md"
				lang={entry.lang || undefined}
			>
				{entry.title}
			</span>
			<span className={`sm:hidden block mt-1 ${META}`}>
				{[category, date].filter(Boolean).join(" · ")}
			</span>
		</span>
		<span className={`hidden sm:block ${META}`}>{category}</span>
		<span className="flex items-center gap-4">
			<span className={`hidden sm:inline ${META} tabular-nums`}>{date}</span>
			<span
				className={`font-mono text-xl transition-transform duration-200 ${open ? "text-pp-accent-text translate-x-1" : "text-pp-muted"}`}
				aria-hidden="true"
			>
				→
			</span>
		</span>
	</div>
);

/** The dark panel that unfolds when the card is open: cover, summary, Read, reading time. */
const JournalCardPanel = ({ entry, open }) => {
	const { t } = useLocales();
	return (
		// grid-rows 0fr → 1fr animates to the panel's real height.
		<div
			className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
			style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
			aria-hidden={!open}
		>
			<div className="overflow-hidden">
				<div className="bg-pp-ink px-4 sm:px-8 lg:px-12 pt-6 pb-8">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<figure>
							<Cover
								type="journal"
								src={entry.image}
								alt={entry.imageAlt}
								org="JOURNAL"
								dark
							/>
							{entry.imageCaption && (
								<figcaption className="mt-3 font-mono text-pp-label uppercase tracking-pp-label text-pp-on-slab-muted">
									{entry.imageCaption}
								</figcaption>
							)}
						</figure>
						<div className="flex flex-col justify-between gap-6">
							{entry.description && (
								<p
									className="text-pp-on-slab-muted text-base leading-relaxed line-clamp-4"
									lang={entry.lang || undefined}
								>
									{entry.description}
								</p>
							)}
							<div className="flex items-center gap-4">
								<span className="font-mono text-pp-label uppercase tracking-pp-ui px-4 py-2.5 bg-pp-accent-text text-pp-on-slab">
									{t("ui.cardRead")} →
								</span>
								<span className="font-mono text-pp-label uppercase tracking-pp-label text-pp-on-slab-muted">
									{t("ui.cardReadingTime", {
										minutes: entry.readingMinutes || 1,
									})}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

/**
 * A Journal entry in the list, shared with the admin preview so what you edit
 * is what visitors see. Collapsed it is one line; `open` adds the accent edge
 * and unfolds the panel. On the site the whole card is one link and hovering
 * or focusing it calls `onActivate`; with `preview` nothing navigates.
 */
const JournalCard = ({ entry, index = 0, open = false, preview = false, onActivate }) => {
	const { t, currentLang } = useLocales();
	const category = categoryLabel(entry.category, t, currentLang);
	const date = formatDate(entry.date, currentLang, "month").toUpperCase();
	const className = `group block border-b border-pp-rule border-l-2 transition-colors duration-200 active:bg-pp-surface ${open ? "border-l-pp-accent" : "border-l-transparent"}`;
	const body = (
		<>
			<JournalCardLine
				entry={entry}
				index={index}
				open={open}
				category={category}
				date={date}
			/>
			<JournalCardPanel entry={entry} open={open} />
		</>
	);

	if (preview) return <div className={className}>{body}</div>;
	return (
		<Link to={entry.href} onMouseEnter={onActivate} onFocus={onActivate} className={className}>
			{body}
		</Link>
	);
};

const entryShape = PropTypes.shape({
	href: PropTypes.string,
	title: PropTypes.string,
	category: PropTypes.string,
	date: PropTypes.string,
	description: PropTypes.string,
	image: PropTypes.string,
	imageAlt: PropTypes.string,
	imageCaption: PropTypes.string,
	readingMinutes: PropTypes.number,
	lang: PropTypes.string,
});

JournalCardLine.propTypes = {
	entry: entryShape.isRequired,
	index: PropTypes.number.isRequired,
	open: PropTypes.bool.isRequired,
	category: PropTypes.string,
	date: PropTypes.string,
};
JournalCardPanel.propTypes = {
	entry: entryShape.isRequired,
	open: PropTypes.bool.isRequired,
};
JournalCard.propTypes = {
	entry: entryShape.isRequired,
	index: PropTypes.number,
	open: PropTypes.bool,
	preview: PropTypes.bool,
	onActivate: PropTypes.func,
};

export default JournalCard;
